import BusinessIcon from "@mui/icons-material/Business";
import CheckIcon from "@mui/icons-material/Check";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import HomeIcon from "@mui/icons-material/Home";
import { Avatar, Box, Button, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import { EditOutlined } from "@mui/icons-material";
import { ConfirmDialog } from "../../../../common/ConfirmDialog";
import { colors, roleColors, alphaVar } from "../../../../ui/colors";
import { useUpdateJobAliasMutation } from "../../hooks/useUpdateJobAlias";
import { useDeleteJobAction } from "../hooks/useDeleteJobAction";
import { JobCardBadge } from "./JobCardBadge";

interface Props {
  company: any;
  job: any;
  isResidential: boolean;
  workScope: any;
  onJobUpdated?: () => void;
}

export const JobCardHeader: React.FC<Props> = ({ company, job, isResidential, workScope, onJobUpdated }) => {
  const isBasic = job?.type === "basic";
  const currentRole = isBasic ? { base: colors.gray[400] } : isResidential ? roleColors.residential : roleColors.commercial;
  const updateJobAliasMutation = useUpdateJobAliasMutation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const deleteJobMutation = useDeleteJobAction();

  const [isEditingAlias, setIsEditingAlias] = useState(false);
  const [aliasValue, setAliasValue] = useState(job?.alias || "");
  const [isHovered, setIsHovered] = useState(false);
  const [isAddAliasHovered, setIsAddAliasHovered] = useState(false);
  useEffect(() => {
    setAliasValue(job?.alias || "");
  }, [job?.alias]);
  const handleSaveAlias = async (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const cleanAlias = aliasValue.trim();

    if (cleanAlias === job?.alias?.trim()) {
      setIsEditingAlias(false);
      return;
    }

    try {
      await updateJobAliasMutation.mutateAsync({
        jobId: job.id,
        alias: cleanAlias || null,
      });

      onJobUpdated?.();
      setIsEditingAlias(false);
    } catch (error) {
      console.error("Error updating job alias:", error);
    }
  };

  const handleCancelAlias = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAliasValue(job?.alias || "");
    setIsEditingAlias(false);
  };

  const alias = job?.alias?.trim() || "";
  const hasAlias = Boolean(alias);

  const originalServiceName =
    workScope?.typeOfService?.name?.trim() ||
    workScope?.serviceName?.trim() ||
    (typeof workScope?.service === "string" ? workScope.service.trim() : workScope?.service?.name?.trim()) ||
    job?.serviceName?.trim() ||
    (typeof job?.service === "string" ? job.service.trim() : job?.service?.name?.trim()) ||
    "Service";

  const serviceNameOverride =
    workScope?.serviceNameOverride?.trim() || job?.scopeOfWork?.serviceNameOverride?.trim() || job?.serviceNameOverride?.trim() || "";

  const hasServiceAlias = Boolean(serviceNameOverride);

  const serviceName = serviceNameOverride || originalServiceName;

  const jobDisplayId = job?.id ?? job?.jobId ?? job?.serviceOrderId;
  const fallbackJobTitle = jobDisplayId ? `Job #${jobDisplayId}` : "Unnamed job";

  const primaryTitle = hasAlias ? alias : fallbackJobTitle;
  const shouldShowSecondTitle = Boolean(serviceName);
  const normalizeJobLabel = (value?: string | number | null) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/^job#?/, "");

  const shouldShowJobIdText =
    Boolean(jobDisplayId) &&
    hasAlias &&
    normalizeJobLabel(alias) !== normalizeJobLabel(jobDisplayId) &&
    normalizeJobLabel(alias) !== normalizeJobLabel(fallbackJobTitle);

  const jobTypeLabel = isBasic ? "Basic" : "Advanced";
  const clientName = job?.client?.alias || [job?.client?.firstName, job?.client?.lastName].filter(Boolean).join(" ") || "No client";
  const clientTypeLabel = job?.client?.type === "residencial" ? "residential" : job?.client?.type || ""; // protecciòn ?

  return (
    <Box
      display="flex"
      width={{ xs: "100%", sm: "fit-content" }}
      justifyContent="center"
      alignItems="center"
      pr={{ xs: 0, sm: 6 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: alphaVar(currentRole.base, 0.15),
            color: currentRole.base,
            border: `2px solid ${alphaVar(currentRole.base, 0.3)}`,
          }}
        >
          {isBasic ? <CleaningServicesIcon fontSize="small" /> : isResidential ? <HomeIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
        </Avatar>

        <Box>
          <Box display="flex" flexDirection="column" gap={0.5} sx={{ minHeight: 18 }}>
            {/* --- ACTIVE EDITING MODE --- */}
            {isEditingAlias ? (
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
                <TextField
                  value={aliasValue}
                  onChange={(e) => setAliasValue(e.target.value)}
                  placeholder="Enter alias..."
                  size="small"
                  variant="standard"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveAlias(e);
                    if (e.key === "Escape") handleCancelAlias(e);
                  }}
                  sx={{
                    input: {
                      fontSize: "0.92rem",
                      fontWeight: 600,
                      color: colors.gray.base,
                      padding: "2px 0px",
                    },
                  }}
                />

                <Tooltip title="Save Alias" arrow>
                  <IconButton
                    size="small"
                    disabled={updateJobAliasMutation.isPending}
                    onClick={(e) => handleSaveAlias(e)}
                    sx={{ color: colors.success?.base || "green", p: "4px" }}
                  >
                    <CheckIcon sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Cancel" arrow>
                  <IconButton size="small" onClick={(e) => handleCancelAlias(e)} sx={{ color: colors.gray[400], p: "4px" }}>
                    <CloseIcon sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              /* --- NORMAL VIEW MODE --- */
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  minHeight: 28,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    color: colors.gray.base,
                    mb: 0,
                    lineHeight: 1.25,
                    fontSize: { xs: "0.8rem", sm: "0.92rem" },
                    px: isAddAliasHovered ? 1 : 0,
                    pt: 0.25,
                    borderRadius: 1,
                    bgcolor: !hasAlias && isAddAliasHovered ? alphaVar(colors.orange.base, 0.08) : "transparent",
                    boxShadow: !hasAlias && isAddAliasHovered ? `inset 0 0 0 1px ${alphaVar(colors.orange.base, 0.18)}` : "none",
                    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
                  }}
                >
                  {primaryTitle}
                </Typography>

                {hasAlias && (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Tooltip title="Edit Alias" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAliasValue(job?.alias || "");
                          setIsEditingAlias(true);
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          p: 0,
                          opacity: isHovered ? 1 : 0,
                          pointerEvents: isHovered ? "auto" : "none",
                          color: "primary.main",
                          transition: "opacity 0.15s ease",
                          "&:hover": {
                            bgcolor: "rgba(25, 118, 210, 0.08)",
                          },
                        }}
                      >
                        <EditOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )}

            {/* --- "+ ADD ALIAS" BUTTON --- */}
            {!hasAlias && !isEditingAlias && (
              <Tooltip title="Add a custom alias for this job" placement="right" arrow>
                <Button
                  variant="text"
                  size="small"
                  onMouseEnter={() => setIsAddAliasHovered(true)}
                  onMouseLeave={() => setIsAddAliasHovered(false)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAddAliasHovered(false);
                    setIsEditingAlias(true);
                  }}
                  sx={{
                    alignSelf: "flex-start",
                    p: 0,
                    minWidth: 0,
                    fontSize: "0.72rem",
                    color: colors.orange.base,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                  }}
                >
                  + Add alias
                </Button>
              </Tooltip>
            )}

            {shouldShowSecondTitle && !isEditingAlias && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "center", sm: "flex-start" },
                  gap: 0.15,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{
                    color: colors.gray[600],
                    mb: 0,
                    lineHeight: 1.25,
                    fontSize: { xs: "0.72rem", sm: "0.82rem" },
                  }}
                >
                  {hasServiceAlias ? "Service alias" : "Service"}: {serviceName}
                </Typography>

                {hasServiceAlias && originalServiceName && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.gray[400],
                      lineHeight: 1.2,
                      fontSize: { xs: "0.62rem", sm: "0.7rem" },
                    }}
                  >
                    Original service: {originalServiceName}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1} sx={{ flexDirection: { xs: "column", sm: "row" }, flexWrap: "wrap", mt: 0.5 }}>
            <InfoChip label="Client" value={clientName} />
            <InfoChip label="Client type" value={clientTypeLabel} />
            <InfoChip label="Job type" value={jobTypeLabel} />

            {!isBasic && <JobCardBadge isResidential={isResidential} />}

            {shouldShowJobIdText && (
              <Typography variant="body2" fontWeight={500} sx={{ color: colors.gray[700], fontSize: { xs: "0.62rem", sm: "0.72rem" } }}>
                Job #{jobDisplayId}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Delete Button */}
      <Box sx={{ position: "absolute", top: 15, right: 15 }}>
        <IconButton
          sx={{ "&:hover": { color: colors.danger.base } }}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteDialogOpen(true);
          }}
        >
          <DeleteIcon sx={{ color: colors.gray[200] }} />
        </IconButton>
      </Box>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        title="Delete Job"
        description={`Are you sure you want to delete job #${job?.id}? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={() => deleteJobMutation.mutate(job.id)}
        onCancel={() => setIsDeleteDialogOpen(false)}
        stopPropagation
      />
    </Box>
  );
};

const InfoChip = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value || value === "-") return null;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.35,
        px: 0.9,
        py: 0.35,
        borderRadius: "999px",
        bgcolor: alphaVar(colors.orange.base, 0.07),
        border: `1px solid ${alphaVar(colors.orange.base, 0.16)}`,
        fontSize: { xs: "0.58rem", sm: "0.68rem" },
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      <Typography component="span" sx={{ fontSize: "inherit", fontWeight: 800, color: colors.orange.base }}>
        {label}:
      </Typography>
      <Typography component="span" sx={{ fontSize: "inherit", fontWeight: 600, color: colors.gray[700], textTransform: "capitalize" }}>
        {value}
      </Typography>
    </Box>
  );
};
